type ScoreTemplateAction = {
  key: string;
  label: string;
  event_type: string;
  scoreboard_changes?: Record<string, number>;
};

type ScoreTemplateField = {
  key: string;
  label: string;
  kind?: string;
  min?: number;
  step?: number;
};

type ScoreTemplate = {
  sport_key?: string;
  sport_name?: string;
  label?: string;
  scoring_format?: string;
  periods?: Array<{ key: string; label: string }>;
  default_totals?: Record<string, any>;
  scoreboard_fields?: ScoreTemplateField[];
  quick_actions?: ScoreTemplateAction[];
  rules?: Record<string, any>;
  live_state_defaults?: Record<string, any>;
};

const FALLBACK_TEMPLATE: ScoreTemplate = {
  sport_key: "custom",
  sport_name: "Sport",
  label: "Live scorer",
  scoring_format: "custom",
  periods: [{ key: "period-1", label: "1st Period" }],
  default_totals: {
    points: 0,
    display_score: "0",
  },
  scoreboard_fields: [{ key: "points", label: "Points", kind: "number", min: 0, step: 1 }],
  quick_actions: [{ key: "score", label: "Score", event_type: "score", scoreboard_changes: { points: 1 } }],
  rules: {
    primary_stat_label: "Points",
  },
  live_state_defaults: {
    current_period: "1st Period",
  },
};

const numberValue = (value: unknown) => {
  const numericValue = Number(value ?? 0);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const getScoreTemplate = (matchOrScoresheet: any): ScoreTemplate => {
  const template = matchOrScoresheet?.score_template;

  if (!template || typeof template !== "object") {
    return FALLBACK_TEMPLATE;
  }

  return {
    ...FALLBACK_TEMPLATE,
    ...template,
    default_totals: {
      ...(FALLBACK_TEMPLATE.default_totals || {}),
      ...(template.default_totals || {}),
    },
    scoreboard_fields: template.scoreboard_fields || FALLBACK_TEMPLATE.scoreboard_fields,
    quick_actions: template.quick_actions || FALLBACK_TEMPLATE.quick_actions,
    periods: template.periods || FALLBACK_TEMPLATE.periods,
    rules: {
      ...(FALLBACK_TEMPLATE.rules || {}),
      ...(template.rules || {}),
    },
    live_state_defaults: {
      ...(FALLBACK_TEMPLATE.live_state_defaults || {}),
      ...(template.live_state_defaults || {}),
    },
  };
};

export const normalizeTeamTotals = (template: ScoreTemplate, totals?: Record<string, any>) => {
  const nextTotals = {
    ...(template.default_totals || {}),
    ...(totals || {}),
  };

  return {
    ...nextTotals,
    display_score: formatTeamDisplayScore(template, nextTotals),
  };
};

export const buildCurrentScoreboard = (
  template: ScoreTemplate,
  homeTotals?: Record<string, any>,
  awayTotals?: Record<string, any>,
) => ({
  home: normalizeTeamTotals(template, homeTotals),
  away: normalizeTeamTotals(template, awayTotals),
});

export const formatTeamDisplayScore = (template: ScoreTemplate, totals?: Record<string, any>) => {
  const scoreboard = {
    ...(template.default_totals || {}),
    ...(totals || {}),
  };
  const sportKey = template.sport_key || "custom";

  switch (sportKey) {
    case "cricket":
      return `${numberValue(scoreboard.runs)}/${numberValue(scoreboard.wickets)}`;
    case "rugby":
    case "basketball":
    case "athletics":
    case "swimming":
      return String(numberValue(scoreboard.points));
    case "football":
    case "hockey":
    case "netball":
    case "water-polo":
    case "lacrosse":
      return String(numberValue(scoreboard.goals));
    case "baseball":
    case "softball":
      return String(numberValue(scoreboard.runs));
    case "tennis":
    case "padel":
    case "squash":
    case "badminton":
    case "table-tennis":
    case "volleyball": {
      const setsWon = numberValue(scoreboard.sets_won);
      return `${setsWon} set${setsWon === 1 ? "" : "s"}`;
    }
    default:
      return String(scoreboard.display_score ?? numberValue(scoreboard.points));
  }
};

export const applyQuickActionToTotals = (
  template: ScoreTemplate,
  totals: Record<string, any>,
  action?: ScoreTemplateAction,
) => {
  const nextTotals = {
    ...(totals || {}),
  };

  Object.entries(action?.scoreboard_changes || {}).forEach(([key, increment]) => {
    nextTotals[key] = Math.max(0, numberValue(nextTotals[key]) + numberValue(increment));
  });

  nextTotals.display_score = formatTeamDisplayScore(template, nextTotals);
  return nextTotals;
};

export const buildLiveState = (template: ScoreTemplate, liveState?: Record<string, any>) => ({
  ...(template.live_state_defaults || {}),
  ...(liveState || {}),
});

export const buildScoresheetPayload = ({
  homeTotals,
  awayTotals,
  liveState,
  notes,
  scoringFormat,
  status,
  template,
}: {
  homeTotals: Record<string, any>;
  awayTotals: Record<string, any>;
  liveState: Record<string, any>;
  notes?: string;
  scoringFormat?: string;
  status?: string;
  template: ScoreTemplate;
}) => ({
  status: status || "draft",
  scoring_format: scoringFormat || template.scoring_format || "custom",
  notes: notes || "",
  home_totals: normalizeTeamTotals(template, homeTotals),
  away_totals: normalizeTeamTotals(template, awayTotals),
  live_state: buildLiveState(template, liveState),
  rules: template.rules || {},
  metadata: {
    template_key: template.sport_key || "custom",
  },
});

export const getAvailablePlayersByTeam = ({
  rosters,
  teamPlayersByTeam,
}: {
  rosters?: any[];
  teamPlayersByTeam?: Record<string, any[]>;
}) => {
  const rosterPlayersByTeam = Object.fromEntries(
    (rosters || []).map((roster: any) => [
      String(roster.school_team_id),
      (roster.school_match_roster_players || []).map((player: any) => ({
        id: player.player_id,
        full_name: player.player_name,
        position: player.position,
        jersey_number: player.jersey_number,
      })),
    ]),
  );

  return {
    ...(teamPlayersByTeam || {}),
    ...rosterPlayersByTeam,
  };
};
